/**
 * TOSS Web Application — the head of the side navigation
 *
 * Logo, product name and the collapse toggle. It was written twice, nearly word for word: the
 * two copies differed in the link, the title and whether there was a second line, which is
 * exactly what the props below are.
 *
 * Collapsed, the logo IS the toggle: hovering it swaps the mark for the panel icon. Without a
 * toggle to offer (the tablet, where the collapse is forced) it stays a link home.
 *
 * The height matches the top bar's so the mark lines up with whatever the bar puts in its left
 * corner, and it carries the same safe-area padding: both surfaces are installable.
 *
 * The mark is drawn white by `--sidebar-logo-filter`, not by a second asset: `logo-icon.svg` is a
 * single colour (`#3F4CBF` twice), so `brightness(0) invert(1)` gives a clean silhouette — and the
 * brand blue it ships in would sit on a dark blue rail almost invisibly.
 */

import { Link } from 'react-router-dom';
import { PanelLeft } from 'lucide-react';
import { Tooltip } from './Tooltip';

// ============================================================================
// Types
// ============================================================================

export interface SidebarBrandProps {
  /** Where the mark leads — each surface has its own home. */
  to: string;
  /** Product name. A node, so a surface can colour part of it. */
  title: React.ReactNode;
  /** Optional second line under the title. */
  subtitle?: string;
  /** Accessible name of the link. */
  label: string;
  collapsed?: boolean;
  /** Omitted where the collapse is forced — the tablet has no choice to offer. */
  onToggleCollapsed?: () => void;
  /** Closes the mobile drawer when the mark is clicked. */
  onNavigate?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function SidebarBrand({
  to,
  title,
  subtitle,
  label,
  collapsed = false,
  onToggleCollapsed,
  onNavigate,
}: SidebarBrandProps): JSX.Element {
  return (
    <div className="flex items-center gap-2 h-[calc(56px+env(safe-area-inset-top,0px))] lg:h-[calc(64px+env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)] px-3 flex-shrink-0">
      {collapsed ? (
        onToggleCollapsed ? (
          <Tooltip content="Открыть боковую панель" side="right">
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="group relative mx-auto flex items-center justify-center w-10 h-10 rounded-lg hover:bg-[var(--color-bg-secondary)] transition-colors"
              aria-label="Открыть боковую панель"
            >
              <img
                src="/assets/logo-icon.svg"
                alt="ТОСС"
                className="absolute h-7 w-auto transition-opacity duration-150 group-hover:opacity-0 [filter:var(--sidebar-logo-filter)]"
              />
              <span className="absolute inline-flex items-center justify-center text-[var(--color-text-primary)] opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                <PanelLeft className="w-5 h-5" />
              </span>
            </button>
          </Tooltip>
        ) : (
          <Link to={to} onClick={onNavigate} className="mx-auto flex items-center" aria-label={label}>
            <img
              src="/assets/logo-icon.svg"
              alt="ТОСС"
              className="h-7 w-auto [filter:var(--sidebar-logo-filter)]"
            />
          </Link>
        )
      ) : (
        <>
          <Link
            to={to}
            onClick={onNavigate}
            className="flex items-center gap-2 min-w-0 flex-1"
            aria-label={label}
          >
            <img
              src="/assets/logo-icon.svg"
              alt=""
              className="h-8 w-auto flex-shrink-0 [filter:var(--sidebar-logo-filter)]"
              aria-hidden="true"
            />
            <span className="flex flex-col min-w-0 leading-tight">
              <span className="text-lg font-bold truncate text-[var(--color-text-primary)]">
                {title}
              </span>
              {subtitle && (
                <span className="text-xs truncate text-[var(--color-text-secondary)]">
                  {subtitle}
                </span>
              )}
            </span>
          </Link>

          {onToggleCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors"
              aria-label="Свернуть боковую панель"
              title="Свернуть"
            >
              <PanelLeft className="w-5 h-5" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default SidebarBrand;
